import { useState, useRef, useEffect } from 'react';
import { AccordionSummary, AccordionDetails, Button, Typography, Box, Grid2 as Grid, Alert, Stack } from '@mui/material';
import CustomAccordion from '../../components/Accordian/accordian';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Autocomplete, useLoadScript } from '@react-google-maps/api';
import { Libraries } from '@react-google-maps/api/dist/utils/make-load-script-url';
import SearchIcon from '@mui/icons-material/Search';
import { useGetFacilityDistanceSearchQuery } from '../../api/endpoints/apiConfig';
import { GridData } from '../../models/GridData';
import '../../assets/styles/header.css';
import { logoEnum } from '../../components/Common/headerenum';
import { toast } from "react-toastify";
import localConfig from '../../config';

const FacilitySearch = () => {
    const navigate = useNavigate();
    const libraries: Libraries = ['places'];
    const apiKey = localConfig.googleMapKey || '';
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const [gridData, setGriddata] = useState<GridData[]>([]);
    const [operatorids, setoperatorid] = useState(0);
    const selectedOperatorId: string | null = sessionStorage.getItem('operatorId');
    const { data: facilities, isLoading } = useGetFacilityDistanceSearchQuery(Number(selectedOperatorId));
    const SettingData = sessionStorage.getItem('setting');
    const Settings = SettingData ? JSON.parse(SettingData) : [];
    const Radius_miles = Settings.search_radius_miles || 50;
    useEffect(() => {
        if (selectedOperatorId !== null) {
            const operatorIdNumber: number = Number(selectedOperatorId);
            setoperatorid(operatorIdNumber);
        } else {
            console.error("operatorId not found in sessionStorage");
        }
    }, [selectedOperatorId]);
    // eslint-disable-next-line
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: apiKey,
        libraries,
    })
    // Helper function to convert degconst to radians
    const toRadians = (degrees: number): number => degrees * (Math.PI / 180);
    const Distance = (lat: number, lng: number) => {
        const selectedOperatorId = sessionStorage.getItem('operatorId');
        if (!selectedOperatorId) {
            throw new Error("Operator ID is null or not found in session storage.");
        }
        // Assuming you want to use the session value as `operatorids`
        const operatorids = Number(selectedOperatorId);
        if (!operatorids) {
            throw new Error("Operator ID is null or invalid.");
        }

        if (facilities) {
            const filteredFacilities = facilities.facilities.filter(facility => facility.operator_id === operatorids);
            const updatedGridData = filteredFacilities.map((facility) => {
                const lat1 = facility.latitude ?? 0;
                const lon1 = facility.longitude ?? 0;
                const R = 6371; // Radius of the Earth in kilometers
                const dLat = toRadians(lat - lat1);
                const dLon = toRadians(lng - lon1);
                const a =
                    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat)) *
                    Math.sin(dLon / 2) * Math.sin(dLon / 2);
                const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                const distanceInKm = R * c;
                // Convert distance from kilometers to miles
                const distanceInMiles = distanceInKm * 0.621371;
                return {
                    id: facility.id,
                    distance: Number(distanceInMiles.toFixed(2)),
                    operator_id: facility.operator_id,
                    name: facility.name,
                    city: facility.city,
                    address: facility.address,
                    state: facility.state,
                    distanceInKm: distanceInKm,
                    location_code: facility.location_code,
                    phone: facility.phone,
                };
            });
            // Filter facilities within Radius_miles range from the setting data 
            const filterdata = updatedGridData.filter(item => item.distanceInKm <= Radius_miles);
            setGriddata(filterdata);
        }
    };
    const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
        autocompleteRef.current = autocomplete;
    };
    const onPlaceChanged = () => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();
            if (place && place.geometry && place.geometry.location) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                Distance(lat, lng);
            } else {
                toast.error('Place does not have location data.');
                setGriddata([]);
            }
        } else {
            toast.error('Place does not have location data.');
            setGriddata([]);
        }
    };
    const formatPhoneNumber = (phone: string): string => {
        if (phone.length < 10) return 'Invalid Number'; // Check for valid phone number length
        return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    };
    const handleView = (row: { id: string | number }) => {
        navigate(`/Dashboard/${row.id}`);
    };
    const colDefs: GridColDef[] = [
        { field: 'distance', headerName: 'Distance (mi)', width: 120 },
        { field: 'name', headerName: 'Name', minWidth: 300 },
        { field: 'address', headerName: 'Address', width: 150 },
        { field: 'city', headerName: 'City', width: 120 },
        { field: 'state', headerName: 'State', minWidth: 20 },
        {
            field: 'phone', headerName: 'Phone', width: 150, valueGetter: (params) => {
                const phone = params; // Accessing the phone directly
                return phone ? formatPhoneNumber(phone)
                    : 'N/A'; // Assuming formatPhoneNumber is a valid function
            },
        },
        {
            field: 'action',
            headerName: 'Action',
            flex: 1,
            renderCell: (params) => (
                <Button size="small" variant="contained" onClick={() => handleView(params.row)}>
                    View
                </Button>
            ),
        },
    ];
    const CustomNoResultsOverlay = () => (
        <Box textAlign="center" p={2}>
            <Typography variant="h6">No Facilities Found</Typography>
        </Box>
    );
    const Header = () => {
        return (
            <Box p={1} bgcolor={'common.white'} borderRadius={'5px'} boxShadow={1}>
                <Stack direction={'row'} alignItems={'center'} useFlexGap flexWrap={'wrap'} spacing={2} justifyContent={'center'}>
                    <Typography
                        component="div"
                        className={logoEnum[operatorids as keyof typeof logoEnum]?.logname}
                        title='Store-Space'
                        sx={{ width: '250px', height: '200px' }}
                    >
                    </Typography>
                    <Box>
                    </Box>
                </Stack>
            </Box>
        );
    }
    return (
        <>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Header />
                </Grid>
            </Grid>
            <Box my={2}>
                <Alert severity="info">Enter an address below to find nearby facilities.  The search radius is {Radius_miles} miles.</Alert>
            </Box>
            {/* Accordion Section */}
            <CustomAccordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
                    <Typography variant="h3" color="text.secondary">Find a Facility</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box display="flex" justifyContent="center" alignItems="center" width="100%">
                        {isLoaded && (
                            <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
                                <div style={{ position: 'relative', width: '500px' }}>
                                    <SearchIcon
                                        style={{
                                            position: 'absolute',
                                            left: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: '#aaa',
                                        }}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Enter an address"
                                        style={{
                                            width: '100%',
                                            height: '50px',
                                            padding: '10px 10px 10px 40px',
                                            borderRadius: '4px',
                                            border: '1px solid #ccc',
                                            fontSize: '16px',
                                        }}
                                    />
                                </div>
                            </Autocomplete>
                        )}
                    </Box>
                    <Box py={2} style={{ width: '100%', height: '400px' }}>
                        <DataGrid
                            disableColumnSorting={true}
                            rows={gridData}
                            columns={colDefs}
                            loading={isLoading}
                            slots={{
                                noResultsOverlay: CustomNoResultsOverlay,
                            }}
                        />
                    </Box>
                </AccordionDetails>
            </CustomAccordion>
        </>
    );
};

export default FacilitySearch;
